import axios, { type AxiosError, type AxiosInstance } from "axios";
import { supabase } from "../stores/auth.store";
import type {
  ErrorAnalysisItem,
  ErrorAnalysisSummary,
  FilterOptions,
  PaginatedResponse,
  ScoringBreakdown,
  SkillCheckAdminDetail,
  SkillCheckAdminItem,
  SkillCheckFilters,
  SkillCheckJobStepDetail,
  SkillCheckStats,
} from "../types/admin.types";

const API_BASE_URL = import.meta.env.VITE_ADMIN_API_URL as string;

class ApiService {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // リクエストインターセプター: 認証トークンを付与
    this.client.interceptors.request.use(async (config) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.access_token) {
        config.headers.Authorization = `Bearer ${session.access_token}`;
      }
      return config;
    });

    // レスポンスインターセプター: エラーハンドリング
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<{ message?: string }>) => {
        if (error.response?.status === 401) {
          // 未認証の場合はログインページにリダイレクト
          window.location.href = "/admin/login";
        } else if (error.response?.status === 403) {
          throw new Error("アクセス権限がありません");
        }
        throw new Error(
          error.response?.data?.message ?? error.message ?? "APIエラーが発生しました",
        );
      },
    );
  }

  // スキルチェック一覧取得
  async listSkillChecks(
    filters: SkillCheckFilters = {},
    limit = 20,
    offset = 0,
  ): Promise<PaginatedResponse<SkillCheckAdminItem>> {
    const params = new URLSearchParams();
    if (filters.status) {
      params.append("status", filters.status);
    }
    if (filters.matchLevel !== undefined) {
      params.append("matchLevel", String(filters.matchLevel));
    }
    if (filters.since) {
      params.append("since", filters.since);
    }
    if (filters.until) {
      params.append("until", filters.until);
    }
    if (filters.teamId) {
      params.append("teamId", filters.teamId);
    }
    if (filters.companyId) {
      params.append("companyId", filters.companyId);
    }
    if (filters.jobPostingId) {
      params.append("jobPostingId", filters.jobPostingId);
    }
    if (filters.query) {
      params.append("query", filters.query);
    }
    if (filters.sortOrder) {
      params.append("sortOrder", filters.sortOrder);
    }
    params.append("limit", String(limit));
    params.append("offset", String(offset));

    const response = await this.client.get<PaginatedResponse<SkillCheckAdminItem>>(
      `/admin/skill-checks?${params.toString()}`,
    );
    return response.data;
  }

  // スキルチェック詳細取得
  async getSkillCheckDetail(jobId: string): Promise<SkillCheckAdminDetail> {
    const response = await this.client.get<SkillCheckAdminDetail>(`/admin/skill-checks/${jobId}`);
    return response.data;
  }

  // ジョブステップ（LLM入出力）取得
  async getSkillCheckSteps(jobId: string): Promise<SkillCheckJobStepDetail[]> {
    const response = await this.client.get<{ steps: SkillCheckJobStepDetail[] }>(
      `/admin/skill-checks/${jobId}/steps`,
    );
    return response.data.steps;
  }

  // スコアリング内訳取得
  async getScoringBreakdown(jobId: string): Promise<ScoringBreakdown> {
    const response = await this.client.get<ScoringBreakdown>(
      `/admin/skill-checks/${jobId}/scoring`,
    );
    return response.data;
  }

  // 統計情報取得
  async getStats(since?: string, until?: string): Promise<SkillCheckStats> {
    const params = new URLSearchParams();
    if (since) {
      params.append("since", since);
    }
    if (until) {
      params.append("until", until);
    }

    const response = await this.client.get<SkillCheckStats>(
      `/admin/skill-checks/stats?${params.toString()}`,
    );
    return response.data;
  }

  // エラー分析取得
  async getErrorAnalysis(
    since?: string,
    until?: string,
    errorCode?: string,
    limit = 20,
    offset = 0,
  ): Promise<{
    items: ErrorAnalysisItem[];
    total: number;
    hasMore: boolean;
    summary: ErrorAnalysisSummary;
  }> {
    const params = new URLSearchParams();
    if (since) {
      params.append("since", since);
    }
    if (until) {
      params.append("until", until);
    }
    if (errorCode) {
      params.append("errorCode", errorCode);
    }
    params.append("limit", String(limit));
    params.append("offset", String(offset));

    const response = await this.client.get<{
      items: ErrorAnalysisItem[];
      total: number;
      hasMore: boolean;
      summary: ErrorAnalysisSummary;
    }>(`/admin/skill-checks/errors?${params.toString()}`);
    return response.data;
  }

  // フィルタ用参照データ取得
  async getFilterOptions(): Promise<FilterOptions> {
    const response = await this.client.get<FilterOptions>("/admin/filter-options");
    return response.data;
  }
}

export const apiService = new ApiService();
